import React, { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { zonedTimeToUtc } from 'date-fns-tz';

import { Button } from '@components/common';
import FormWithTabs from '@components/common/FormWithTabs';

import { portalMilestones as portalMilestonesSelector } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import { clearAssociatedTasks, setAssociatedTasks } from '@slices/portalMilestones';

import { projectUsersFormatted } from '@utils/formatter';
import { formatDate, parseStringToDateInMilliseconds } from '@utils/kpi-utils/date';

import { TSelectOption } from '@interfaces/components/componentTypes';
import { IMilestoneFormValues } from '@interfaces/components/form';
import {
	ICreateMilestoneDTO,
	IEditMilestoneDTO,
	IPortalMilestoneDTO,
} from '@interfaces/portal/dto/milestonesManagementDTO';

import { EValidationMode } from '@enums/validationMode';

import { formatTemplateDefault, timezoneUTC } from '@constants/common';
import { tabsNames } from './constants';

import BasicInfo from './BasicInfo';
import Dependencies from './Dependencies';
import Details from './Details';
import { getSchema } from './schema';

import styles from './MilestoneForm.module.scss';

interface IMilestoneFormProps {
	closeModals: () => void;
	createMilestone?: (data: ICreateMilestoneDTO) => Promise<void>;
	defaultProject?: TSelectOption<string>;
	isEditMode?: boolean;
	isViewMode?: boolean;
	portalMilestone?: IPortalMilestoneDTO;
	projectId?: string;
	setIsDirtyFormModal: React.Dispatch<React.SetStateAction<boolean>>;
	toggleCloseConfirmModal: () => void;
	updateMilestone?: (data: IEditMilestoneDTO) => Promise<void>;
}

const MilestoneForm = ({
	projectId,
	closeModals,
	setIsDirtyFormModal,
	createMilestone,
	defaultProject,
	toggleCloseConfirmModal,
	isEditMode,
	isViewMode,
	portalMilestone,
	updateMilestone,
}: IMilestoneFormProps) => {
	const dispatch = useAppDispatch();

	const { associatedTasks } = useAppSelector(portalMilestonesSelector);

	const {
		register,
		handleSubmit,
		formState: { errors, isDirty },
		control,
		setValue,
		watch,
		trigger,
		clearErrors,
		reset,
	} = useForm<IMilestoneFormValues>({
		resolver: yupResolver(getSchema(isEditMode)),
		mode: EValidationMode.all,
		defaultValues: {
			project: defaultProject,
			title: portalMilestone?.title,
			description: portalMilestone?.description,
			status: portalMilestone?.status?.name,
			startDate: portalMilestone?.startDate
				? parseStringToDateInMilliseconds(portalMilestone.startDate)
				: undefined,
			endDate: portalMilestone?.endDate
				? parseStringToDateInMilliseconds(portalMilestone.endDate)
				: undefined,
			assignees: portalMilestone?.assignees && projectUsersFormatted(portalMilestone.assignees),
		},
	});

	useEffect(() => {
		if (portalMilestone?.linkedTasks && (isViewMode || isEditMode)) {
			dispatch(setAssociatedTasks({ associatedTasks: portalMilestone.linkedTasks }));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const clearForm = useCallback(() => {
		reset({
			title: '',
			description: '',
		});
	}, [reset]);

	const isBasicInfoTabError = !!(errors.project || errors.title || errors.description);

	const isDetailsTabError = !!(errors.startDate
		|| errors.endDate
		|| errors.assignees
	);

	const onSubmit = useCallback((values: IMilestoneFormValues) => {
		const projectId = values?.project?.value;
		const status = values.status;

		const data = {
			assignees: values?.assignees?.map(assignee => assignee.value),
			description: values?.description,
			title: values.title,
			startDate: formatDate(zonedTimeToUtc(values.startDate, timezoneUTC), formatTemplateDefault),
			endDate: formatDate(zonedTimeToUtc(values.endDate, timezoneUTC), formatTemplateDefault),
			linkedTasksIds: associatedTasks.map(associatedTask => associatedTask.id),
		};

		if (isEditMode) {
			if (!portalMilestone?.id || !updateMilestone) return;

			void updateMilestone({
				milestoneId: portalMilestone.id,
				dto: {
					...data,
					status,
				},
			});
		} else {
			if (!projectId || !createMilestone) return;

			void createMilestone({
				...data,
				projectId,
			});
		}

		closeModals();
	}, [
		closeModals,
		associatedTasks,
		createMilestone,
		isEditMode,
		updateMilestone,
		portalMilestone?.id,
	]);

	const tabs = useMemo(() => (
		[
			{
				title: 'Basic information',
				value: tabsNames.basicInfo,
				component: BasicInfo,
				isTabError: isBasicInfoTabError,
				props: {
					register,
					errors,
					setValue,
					trigger,
					control,
					isBasicInfoTabError,
					watch,
					isDirty,
					clearForm,
					portalMilestone,
				},
			},
			{
				title: 'Details',
				value: tabsNames.details,
				component: Details,
				isTabError: isDetailsTabError,
				props: {
					errors,
					setValue,
					watch,
					trigger,
					clearErrors,
					isDetailsTabError,
					projectId,
					control,
				},
			},
			{
				title: 'Dependencies',
				value: tabsNames.dependencies,
				component: Dependencies,
				isTabError: false,
				props: {
					projectId,
					onSubmit: handleSubmit(onSubmit),
					watch,
					milestoneId: portalMilestone?.id,
				},
			},
		]
	), [
		control,
		watch,
		register,
		errors,
		setValue,
		trigger,
		isBasicInfoTabError,
		isDirty,
		clearForm,
		isDetailsTabError,
		clearErrors,
		projectId,
		handleSubmit,
		onSubmit,
		portalMilestone,
	]);

	useEffect(() => setIsDirtyFormModal(isDirty), [isDirty, setIsDirtyFormModal]);

	useEffect(() => {
		return () => {
			dispatch(clearAssociatedTasks());
		};
	}, [dispatch]);

	return (
		<>
			<FormWithTabs
				isEditMode={isEditMode}
				isViewMode={isViewMode}
				tabs={tabs}
			/>
			{isEditMode
				&& <div className={styles.buttons}>
					<Button
						onClick={toggleCloseConfirmModal}
						text='Cancel'
						withoutBackground
					/>
					<Button
						onClick={handleSubmit(onSubmit)}
						text='Save'
					/>
				</div>
			}
			{isViewMode
				&& <div className={styles.buttons}>
					<Button
						onClick={closeModals}
						text='Cancel'
					/>
				</div>
			}
		</>
	);
};

export default MilestoneForm;
